import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Search, 
  Filter,
  Plus,
  Edit,
  Trash2,
  Eye,
  Phone,
  Mail,
  MapPin,
  Calendar,
  FileText,
  Download,
  RefreshCw,
  UserPlus,
  Heart,
  Activity
} from 'lucide-react';
import { Card, Button, Badge, Input, Select, Modal } from '../../components/ui';
import AdminSidebar from '../../components/layout/AdminSidebar';
import { patientsAPI, appointmentsAPI, realTimeUpdates } from '../../services/api';
import { formatDate, getRelativeDate } from '../../utils';
import { Patient, Appointment } from '../../types';

const AdminPatients: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [genderFilter, setGenderFilter] = useState('all');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addLoading, setAddLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

  // Load patients from API on component mount
  const loadPatients = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [patientsResult, appointmentsResult] = await Promise.all([
        patientsAPI.getAll(),
        appointmentsAPI.getAll()
      ]);

      if (patientsResult.success && patientsResult.data) {
        setPatients(patientsResult.data);
      } else {
        setError(patientsResult.error || 'Failed to load patients');
      }

      if (appointmentsResult.success && appointmentsResult.data) {
        setAppointments(appointmentsResult.data);
      }
    } catch (err) {
      setError('Network error while loading data');
      console.error('Error loading patients:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPatients();
  }, []);

  // Set up real-time updates for patients
  useEffect(() => {
    const handlePatientsUpdate = (updatedPatients: Patient[]) => {
      setPatients(updatedPatients);
    };

    const handleAppointmentsUpdate = (updatedAppointments: Appointment[]) => {
      setAppointments(updatedAppointments);
    };

    // Subscribe to real-time updates
    realTimeUpdates.subscribe('patients', handlePatientsUpdate, 30000); // Every 30 seconds
    realTimeUpdates.subscribe('appointments', handleAppointmentsUpdate, 30000);

    // Cleanup subscriptions on unmount
    return () => {
      realTimeUpdates.unsubscribe('patients');
      realTimeUpdates.unsubscribe('appointments');
    };
  }, []);

  // Handle adding a new patient
  const handleAddPatient = async (patientData: any) => {
    setAddLoading(true);
    setError(null);
    
    try {
      const result = await patientsAPI.create(patientData);
      
      if (result.success && result.data) {
        setPatients(prev => [result.data, ...prev]);
        setShowAddModal(false);
        // Show success message could be added here
      } else {
        setError(result.error || 'Failed to create patient');
      }
    } catch (err) {
      setError('Network error while creating patient');
      console.error('Error creating patient:', err);
    } finally {
      setAddLoading(false);
    }
  };

  // Handle deleting a patient
  const handleDeletePatient = async (patientId: string) => {
    if (!confirm('Are you sure you want to delete this patient? This action cannot be undone.')) {
      return;
    }

    setDeleteLoading(patientId);
    setError(null);
    
    try {
      const result = await patientsAPI.delete(patientId);
      
      if (result.success) {
        setPatients(prev => prev.filter(p => p.id !== patientId));
        if (selectedPatient?.id === patientId) {
          setSelectedPatient(null);
          setShowDetails(false);
        }
      } else {
        setError(result.error || 'Failed to delete patient');
      }
    } catch (err) {
      setError('Network error while deleting patient');
      console.error('Error deleting patient:', err);
    } finally {
      setDeleteLoading(null);
    }
  };

  // Refresh data manually
  const handleRefresh = () => {
    loadPatients();
  };

  const genderOptions = [
    { value: 'all', label: 'All Genders' },
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' }
  ];

  // Filter patients
  const filteredPatients = patients.filter(patient => {
    const matchesSearch = searchTerm === '' || 
      `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.phone.includes(searchTerm) ||
      patient.id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesGender = genderFilter === 'all' || patient.gender === genderFilter;
    
    return matchesSearch && matchesGender;
  });

  const getPatientAppointments = (patientId: string) => {
    return appointments.filter(apt => apt.patientId === patientId);
  };

  const getPatientStats = (patientId: string) => {
    const patientAppointments = getPatientAppointments(patientId);
    return {
      total: patientAppointments.length,
      completed: patientAppointments.filter(apt => apt.status === 'completed').length,
      upcoming: patientAppointments.filter(apt => 
        new Date(apt.date) >= new Date() && apt.status !== 'cancelled'
      ).length,
      cancelled: patientAppointments.filter(apt => apt.status === 'cancelled').length
    };
  };

  const calculateAge = (dateOfBirth: string) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };



  const handleViewDetails = (patient: Patient) => {
    setSelectedPatient(patient);
    setShowDetails(true);
  };

  const stats = [
    {
      title: 'Total Patients',
      value: patients.length,
      icon: Users,
      color: 'bg-blue-500'
    },
    {
      title: 'New This Month',
      value: patients.filter(p => {
        const createdDate = new Date(p.createdAt);
        const now = new Date();
        return createdDate.getMonth() === now.getMonth() && 
               createdDate.getFullYear() === now.getFullYear();
      }).length,
      icon: UserPlus,
      color: 'bg-green-500'
    },
    {
      title: 'Active Patients',
      value: patients.filter(p => 
        getPatientAppointments(p.id).some(apt => 
          new Date(apt.date) >= new Date() && apt.status !== 'cancelled'
        )
      ).length,
      icon: Activity,
      color: 'bg-purple-500'
    },
    {
      title: 'Average Age',
      value: Math.round(
        patients.reduce((sum, p) => sum + calculateAge(p.dateOfBirth), 0) / patients.length || 0
      ),
      icon: Heart,
      color: 'bg-orange-500'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <AdminSidebar isOpen={sidebarOpen} toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
      
      <div className={`transition-all duration-300 ${sidebarOpen ? 'ml-72' : 'ml-20'}`}>
        {/* Header */}
        <header className="bg-white shadow-sm border-b">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Patient Management</h1>
                <p className="text-gray-600">Manage patient records and information</p>
              </div>
              
              <div className="flex items-center space-x-4">
                <Button variant="outline" onClick={handleRefresh} disabled={loading}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button onClick={() => setShowAddModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Patient
                </Button>
              </div>
            </div>
          </div>
        </header>

        <div className="p-6">
          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              <p className="font-medium">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          )}
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {stats.map((stat, index) => (
              <Card key={index} className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Filters */}
          <Card className="mb-6">
            <div className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
                <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 flex-1">
                  <div className="relative flex-1 max-w-md">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <Input
                      placeholder="Search patients by name, email, phone..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Filter className="w-4 h-4 text-gray-400" />
                    <Select
                      options={genderOptions}
                      value={genderFilter}
                      onChange={setGenderFilter}
                      className="min-w-32"
                    />
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">
                    {filteredPatients.length} patients
                  </span>
                  <Button variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-1" />
                    Export
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* Patients Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-600 mr-2" />
              <span className="text-gray-600">Loading patients...</span>
            </div>
          ) : filteredPatients.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPatients.map((patient) => {
                const stats = getPatientStats(patient.id);
                const age = calculateAge(patient.dateOfBirth);
                
                return (
                  <Card key={patient.id} className="p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <Users className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {patient.firstName} {patient.lastName}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {age} years old • {patient.gender}
                          </p>
                        </div>
                      </div>
                      <Badge variant="primary">
                        {patient.id}
                      </Badge>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <Mail className="w-4 h-4 mr-2" />
                        {patient.email}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Phone className="w-4 h-4 mr-2" />
                        {patient.phone}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="w-4 h-4 mr-2" />
                        {patient.address.city}, {patient.address.state}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="w-4 h-4 mr-2" />
                        Registered {getRelativeDate(patient.createdAt)}
                      </div>
                    </div>

                    {/* Patient Stats */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="text-center">
                        <p className="text-lg font-bold text-blue-600">{stats.total}</p>
                        <p className="text-xs text-gray-600">Total Visits</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-green-600">{stats.completed}</p>
                        <p className="text-xs text-gray-600">Completed</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleViewDetails(patient)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeletePatient(patient.id)}
                          disabled={deleteLoading === patient.id}
                        >
                          {deleteLoading === patient.id ? (
                            <RefreshCw className="w-4 h-4 text-gray-400 animate-spin" />
                          ) : (
                          <Trash2 className="w-4 h-4 text-red-600" />
                          )}
                        </Button>
                      </div>
                      
                      {stats.upcoming > 0 && (
                        <Badge variant="success" className="text-xs">
                          {stats.upcoming} upcoming
                        </Badge>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No patients found</h3>
              <p className="text-gray-600 mb-6">
                {searchTerm || genderFilter !== 'all'
                  ? "No patients match your current filters."
                  : "No patients have been registered yet."
                }
              </p>
              <Button onClick={() => setShowAddModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add First Patient
              </Button>
            </Card>
          )}
        </div>
      </div>

      {/* Patient Details Modal */}
      {showDetails && selectedPatient && (
        <Modal
          isOpen={showDetails}
          onClose={() => setShowDetails(false)}
          title="Patient Details"
        >
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Personal Information</h4>
                <div className="space-y-2">
                  <p><span className="font-medium">Name:</span> {selectedPatient.firstName} {selectedPatient.lastName}</p>
                  <p><span className="font-medium">Age:</span> {calculateAge(selectedPatient.dateOfBirth)} years</p>
                  <p><span className="font-medium">Gender:</span> {selectedPatient.gender}</p>
                  <p><span className="font-medium">Date of Birth:</span> {formatDate(selectedPatient.dateOfBirth)}</p>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Contact Information</h4>
                <div className="space-y-2">
                  <p><span className="font-medium">Email:</span> {selectedPatient.email}</p>
                  <p><span className="font-medium">Phone:</span> {selectedPatient.phone}</p>
                  <p><span className="font-medium">Address:</span> {selectedPatient.address.street}</p>
                  <p><span className="font-medium">City:</span> {selectedPatient.address.city}, {selectedPatient.address.state} {selectedPatient.address.zipCode}</p>
                </div>
              </div>
            </div>

            {/* Emergency Contact */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Emergency Contact</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <p><span className="font-medium">Name:</span> {selectedPatient.emergencyContact.name}</p>
                <p><span className="font-medium">Relationship:</span> {selectedPatient.emergencyContact.relationship}</p>
                <p><span className="font-medium">Phone:</span> {selectedPatient.emergencyContact.phone}</p>
              </div>
            </div>

            {/* Appointment History */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Recent Appointments</h4>
              <div className="space-y-2">
                {getPatientAppointments(selectedPatient.id).slice(0, 5).map((appointment) => (
                  <div key={appointment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{formatDate(appointment.date)}</p>
                      <p className="text-sm text-gray-600">Duration: {appointment.duration} min</p>
                    </div>
                    <Badge variant={appointment.status === 'completed' ? 'success' : 'primary'}>
                      {appointment.status}
                    </Badge>
                  </div>
                ))}
                {getPatientAppointments(selectedPatient.id).length === 0 && (
                  <p className="text-gray-600 text-center py-4">No appointments found</p>
                )}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowDetails(false)}>
                Close
              </Button>
              <Button>
                <Edit className="w-4 h-4 mr-2" />
                Edit Patient
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Add Patient Modal */}
      {showAddModal && (
        <AddPatientModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddPatient}
          loading={addLoading}
        />
      )}
    </div>
  );
};

// Add Patient Modal Component
interface AddPatientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  loading: boolean;
}

const AddPatientModal: React.FC<AddPatientModalProps> = ({ isOpen, onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: 'male',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: '',
    insuranceProvider: '',
    insurancePolicyNumber: '',
    insuranceGroupNumber: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add New Patient">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Information */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="First Name"
              value={formData.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
              required
            />
            <Input
              label="Last Name"
              value={formData.lastName}
              onChange={(e) => handleChange('lastName', e.target.value)}
              required
            />
            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
            />
            <Input
              label="Phone"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              required
            />
            <Input
              label="Date of Birth"
              type="date"
              value={formData.dateOfBirth}
              onChange={(e) => handleChange('dateOfBirth', e.target.value)}
              required
            />
            <Select
              label="Gender"
              value={formData.gender}
              onChange={(e: any) => handleChange('gender', e.target ? e.target.value : e)}
              options={[
                { value: 'male', label: 'Male' },
                { value: 'female', label: 'Female' },
                { value: 'other', label: 'Other' }
              ]}
            />
          </div>
        </div>

        {/* Address */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Address</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Input
                label="Street Address"
                value={formData.streetAddress}
                onChange={(e) => handleChange('streetAddress', e.target.value)}
                required
              />
            </div>
            <Input
              label="City"
              value={formData.city}
              onChange={(e) => handleChange('city', e.target.value)}
              required
            />
            <Input
              label="State"
              value={formData.state}
              onChange={(e) => handleChange('state', e.target.value)}
              required
            />
            <Input
              label="Zip Code"
              value={formData.zipCode}
              onChange={(e) => handleChange('zipCode', e.target.value)}
              required
            />
          </div>
        </div>

        {/* Emergency Contact */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Emergency Contact</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Contact Name"
              value={formData.emergencyContactName}
              onChange={(e) => handleChange('emergencyContactName', e.target.value)}
              required
            />
            <Input
              label="Contact Phone"
              value={formData.emergencyContactPhone}
              onChange={(e) => handleChange('emergencyContactPhone', e.target.value)}
              required
            />
            <div className="md:col-span-2">
              <Input
                label="Relationship"
                value={formData.emergencyContactRelationship}
                onChange={(e) => handleChange('emergencyContactRelationship', e.target.value)}
                required
              />
            </div>
          </div>
        </div>

        {/* Insurance Information */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Insurance Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Insurance Provider"
              value={formData.insuranceProvider}
              onChange={(e) => handleChange('insuranceProvider', e.target.value)}
            />
            <Input
              label="Policy Number"
              value={formData.insurancePolicyNumber}
              onChange={(e) => handleChange('insurancePolicyNumber', e.target.value)}
            />
            <div className="md:col-span-2">
              <Input
                label="Group Number"
                value={formData.insuranceGroupNumber}
                onChange={(e) => handleChange('insuranceGroupNumber', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Create Patient
              </>
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AdminPatients;